import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useObserver, useSubtree } from "subtree.js/react";
import { ArchiveCardModal } from "../../components/ArchiveCardModal";
import { EbayGradeLinks } from "../../components/EbayGradeLinks";
import { ImageCarousel } from "../../components/ImageCarousel";
import { LoadFailed, Loading } from "../../components/LoadState";
import { SetPriceModal } from "../../components/SetPriceModal";
import { StockLevelModal } from "../../components/StockLevelModal";
import { cardTitle, formatCents, gradeLabel } from "../../core/primitives/card-display";
import { formatDate } from "../../core/primitives/format";
import { exactGradeKey } from "../../core/primitives/grades";
import { PriceHistoryModal } from "../../sections/price-history/price-history-modal";
import { PriceHistoryActions } from "../../sections/price-history/price-history-model";
import { CardDetailActions, CardDetailState } from "./card-detail-model";

function Field({ label, value }: { label: string; value: string | number | null | undefined }) {
  const text = value == null || value === "" ? "—" : String(value);
  return (
    <div>
      <div className="text-xs text-faint">{label}</div>
      <div className="truncate text-sm text-white">{text}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-row border border-line bg-surface p-4">
      <h2 className="mb-3 text-sm font-semibold text-white">{title}</h2>
      {children}
    </section>
  );
}

export function CardDetailPage() {
  const state = useSubtree(CardDetailState);
  const actions = useSubtree(CardDetailActions);
  const history = useSubtree(PriceHistoryActions);
  const observer = useObserver();

  const detail = observer.watch(state.detail);
  const status = observer.watch(state.status);
  const busy = observer.watch(state.busy);
  const notice = observer.watch(state.notice);
  const dialog = observer.watch(state.dialog);
  const dialogError = observer.watch(state.dialogError);
  const canEditInventory = observer.watch(state.canEditInventory);

  if (status.kind === "error") {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <LoadFailed title="Couldn't load this card." message={status.message} onRetry={actions.retry} />
        <Link to="/cards" className="btn-secondary btn-sm">
          Back to cards
        </Link>
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <Loading label="Loading the card…" />
      </div>
    );
  }

  const { card } = detail;
  const title = cardTitle(card);
  const images = [card.imageUrl, card.backImageUrl].filter((u): u is string => !!u);
  const onHand = detail.lots.reduce((sum, lot) => sum + lot.quantity, 0);
  const reserved = detail.lots.reduce((sum, lot) => sum + lot.reservedQuantity, 0);
  const grade = gradeLabel(card);
  const gradeKey = exactGradeKey(grade === "Raw" ? "" : grade, card.slabbed);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
      <Link to="/cards" className="text-xs text-muted hover:text-white">
        ← Cards
      </Link>

      <div className="mt-3 mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg font-bold text-white">{title}</h1>
          <p className="text-xs text-faint">{[card.setName, card.parallelType, grade].filter(Boolean).join(" · ")}</p>
        </div>
        {card.currentPriceCents == null ? (
          <span className="badge badge-warn">Not priced</span>
        ) : (
          <span className="text-xl font-semibold text-accent">{formatCents(card.currentPriceCents)}</span>
        )}
      </div>

      {notice && <div className="note note-accent mb-4 text-accent">{notice}</div>}

      <ImageCarousel urls={images} />

      <div className="mb-5 flex flex-wrap gap-2">
        {canEditInventory && (
          <>
            <button type="button" onClick={() => actions.openDialog("price")} disabled={busy} className="btn-chip">
              Set price
            </button>
            <button type="button" onClick={() => void actions.reprice()} disabled={busy} className="btn-chip">
              Refresh price
            </button>
            <button type="button" onClick={() => actions.openDialog("stock")} disabled={busy} className="btn-chip">
              {card.trackStock ? "Stock" : "Track stock"}
            </button>
          </>
        )}
        <button type="button" onClick={() => void history.open(card)} disabled={busy} className="btn-chip">
          History
        </button>
        {canEditInventory && (
          <button type="button" onClick={() => actions.openDialog("archive")} disabled={busy} className="btn-ghost">
            Archive
          </button>
        )}
      </div>

      <div className="space-y-4">
        <Section title="Card">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Field label="Player" value={card.player} />
            <Field label="Team" value={card.team} />
            <Field label="Brand" value={card.brand} />
            <Field label="Year" value={card.year || null} />
            <Field label="Number" value={card.cardNumber} />
            <Field label="Set" value={card.setName} />
            <Field label="Parallel" value={card.parallelType} />
            <Field label="Insert" value={card.insertName} />
            <Field label="Type" value={card.cardType} />
            <Field label="Grade" value={grade} />
            <Field label="Slabbed" value={card.slabbed ? "Yes" : "No"} />
            <Field label="Confirmed" value={card.confirmedAt ? formatDate(card.confirmedAt) : "Not yet reviewed"} />
          </div>
          {card.features.length > 0 && <p className="mt-3 text-xs text-faint">{card.features.join(" · ")}</p>}
        </Section>

        <Section title="Price">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field label="Asking" value={card.currentPriceCents == null ? null : formatCents(card.currentPriceCents)} />
            <Field label="Floor" value={card.floorPriceCents == null ? null : formatCents(card.floorPriceCents)} />
            <Field label="Priced" value={card.lastPricedAt ? formatDate(card.lastPricedAt) : null} />
            <Field label="Sold" value={detail.soldCount} />
          </div>
          {detail.providerPriceCents != null && (
            <p className="mt-3 text-xs text-warn">
              Shopify says {formatCents(detail.providerPriceCents)} — Refresh price will overwrite it.
            </p>
          )}
          {(card.searchLinks || card.scpData) && (
            <div className="mt-4">
              <EbayGradeLinks
                searchLinks={card.searchLinks ?? undefined}
                scpData={card.scpData}
                cardGradeKey={gradeKey}
              />
            </div>
          )}
        </Section>

        <Section title="Stock">
          {!card.trackStock ? (
            <p className="text-sm text-muted">Untracked. This card sells without reserving copies.</p>
          ) : (
            <>
              <div className="mb-3 grid grid-cols-3 gap-3">
                <Field label="Available" value={card.available} />
                <Field label="On hand" value={onHand} />
                <Field label="Reserved" value={reserved} />
              </div>
              {detail.lots.length > 0 && (
                <ul className="divide-y divide-line text-sm">
                  {detail.lots.map((lot) => (
                    <li key={lot.id} className="flex items-center justify-between gap-3 py-2">
                      <span className="truncate text-white">
                        {lot.quantity} × {lot.locationName ?? "No location"}
                        {lot.certNumber && <span className="text-faint"> · cert {lot.certNumber}</span>}
                      </span>
                      <span className="shrink-0 text-xs text-faint">
                        {lot.reservedQuantity > 0 && `${lot.reservedQuantity} reserved · `}
                        {formatDate(lot.acquiredAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
              {detail.openQuotes > 0 && (
                <p className="mt-3 text-xs text-faint">
                  {detail.openQuotes} open customer {detail.openQuotes === 1 ? "quote" : "quotes"}.
                </p>
              )}
            </>
          )}
        </Section>

        <Section title="Listing">
          {detail.channel ? (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              <Field label="Channel" value={detail.channel} />
              <Field
                label="Listed at"
                value={detail.listingPriceCents == null ? null : formatCents(detail.listingPriceCents)}
              />
              <Field label="Product" value={detail.externalProductId} />
            </div>
          ) : (
            <p className="text-sm text-muted">Not listed on any channel.</p>
          )}
        </Section>

        <Section title="Scans">
          {detail.scans.length === 0 ? (
            <p className="text-sm text-muted">No scans on record.</p>
          ) : (
            <ul className="divide-y divide-line text-sm">
              {detail.scans.map((scan) => (
                <li key={scan.id} className="flex items-center gap-3 py-2">
                  <img src={scan.imageUrl} alt="" className="h-10 w-10 shrink-0 rounded-thumb object-cover" />
                  <span className="min-w-0 flex-1 truncate text-white">
                    {scan.kind === "CUSTOMER"
                      ? `Customer${scan.customerName ? ` · ${scan.customerName}` : ""}`
                      : "Shop"}
                  </span>
                  <span className="shrink-0 text-xs text-faint">
                    {scan.review.toLowerCase()} · {formatDate(scan.timestamp)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Section>
      </div>

      {dialog === "price" && (
        <SetPriceModal
          card={card}
          busy={busy}
          error={dialogError}
          onSave={(next) => void actions.savePrices(next)}
          onCancel={actions.closeDialog}
        />
      )}
      {dialog === "stock" && (
        <StockLevelModal
          card={card}
          busy={busy}
          error={dialogError}
          onSave={(change) => void actions.saveStock(change)}
          onCancel={actions.closeDialog}
        />
      )}
      {dialog === "archive" && (
        <ArchiveCardModal
          card={card}
          busy={busy}
          error={dialogError}
          onConfirm={() => void actions.confirmArchive()}
          onCancel={actions.closeDialog}
        />
      )}
      <PriceHistoryModal />
    </div>
  );
}
